import { Editor } from '@tiptap/react';
import {
  Dispatch,
  FunctionComponent,
  memo,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { storyCardApi, StoryCardCategory, User } from '../../../../services';
import { FileAcceptType, useTags } from '../../../hooks';
import { GalleryItem } from '../../../models';
import {
  getApplePodcastMetaData,
  getCssVar,
  isApplePodcastUrl,
  isEmptyString,
  isUrl,
  markdownToHtml,
} from '../../../utils';
import { FilePreview, FileUpload } from '../../File';
import { IconLabel } from '../../IconLabel';
import { TextAreaField } from '../../TextAreaField';
import { TextEditor, TextEditorVariant } from '../../TextEditor';
import { UserMentionPreview } from '../../UserMentionPreview';
import {
  ConvertToLinkPostArgs,
  CreateCardSettingsTabType,
  ShortPostFields,
} from '../CreateCardModal';
import { PostToggleFields, PostToggleFieldsItem } from '../PostToggleFields';

import classNames from 'classnames';
import classes from './ShortPost.module.scss';

interface ShortPostProps {
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  shortPostEditorRef: MutableRefObject<Editor | null>;
  shortPostFields: ShortPostFields;
  setShortPostFields: Dispatch<SetStateAction<ShortPostFields>>;
  setShortPostGalleryItems: (galleryItems: GalleryItem[]) => void;
  convertToLinkPost: (args: ConvertToLinkPostArgs) => void;
  setSettingsOpen: (isOpen: boolean) => void;
  canOpenSettings: boolean;
  setActiveShortPostSettingsTabIndex: (index: number) => void;
  mentionedUsers: User[];
  setUserMentionModalOpen: Dispatch<SetStateAction<boolean>>;
  postInStoryAllowMentions: boolean;
  files?: FileList;
  fileUrl?: string;
  applePodcastUrl?: string;
  category?: StoryCardCategory;
}

type ShortPostToggleFields = {
  [key in keyof ShortPostFields]?: PostToggleFieldsItem;
};

export const SOURCE_MAX_LENGTH = 160;

export const HEADLINE_MAX_LENGTH = 360;

const SUB_HEADLINE_MAX_LENGTH = 360;

export const ShortPost: FunctionComponent<ShortPostProps> = memo(
  ({
    loading,
    setLoading,
    shortPostEditorRef,
    shortPostFields,
    setShortPostFields,
    setShortPostGalleryItems,
    convertToLinkPost,
    setSettingsOpen,
    canOpenSettings,
    setActiveShortPostSettingsTabIndex,
    mentionedUsers,
    setUserMentionModalOpen,
    postInStoryAllowMentions,
    files,
    fileUrl,
    applePodcastUrl,
    category,
  }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'addContent' });

    const [storyCardParseUrl] = storyCardApi.endpoints.storyCardParseUrl.useMutation();

    const { hasContentTags } = useTags();

    const { sourceName, headline, subHeadline, text, gallery } = shortPostFields;

    const hasGallery = useMemo(() => Boolean(gallery.length), [gallery.length]);

    const isCategoryCard = useMemo(() => Boolean(category), [category]);

    const { settings: categorySettings } = { ...category };

    const editableMedia = useMemo(
      () => (isCategoryCard ? categorySettings?.editableMedia : true),
      [categorySettings?.editableMedia, isCategoryCard]
    );

    const hasUserMention = useMemo(
      () => (isCategoryCard ? categorySettings?.userSelection : postInStoryAllowMentions),
      [categorySettings?.userSelection, isCategoryCard, postInStoryAllowMentions]
    );

    const sourceNameRef = useRef<HTMLTextAreaElement>(null);

    const headlineRef = useRef<HTMLTextAreaElement>(null);

    const subHeadlineRef = useRef<HTMLTextAreaElement>(null);

    const shortPostText = useMemo(
      () =>
        markdownToHtml({
          markdown: text
            .split('\n')
            .map((content) => `<p>${content}</p>`)
            .join(''),
          boldTag: 'strong',
          italicTag: 'em',
        }),
      [text]
    );

    const [appleMetaLoading, setAppleMetaLoading] = useState<boolean>(false);

    const [postFieldsToggling, setPostFieldsToggling] = useState<ShortPostToggleFields>({
      sourceName: {
        ref: sourceNameRef,
        title: ctxT('short-post-sourceName'),
        iconId: '',
        hidden: Boolean(isEmptyString(sourceName)),
      },
      headline: {
        ref: headlineRef,
        title: ctxT('short-post-headline'),
        iconId: '',
        hidden: false,
      },
      subHeadline: {
        ref: subHeadlineRef,
        title: ctxT('short-post-subHeadline'),
        iconId: '',
        hidden: Boolean(isEmptyString(subHeadline)),
      },
    });

    const keyDownHandler = useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>, field: keyof ShortPostFields) => {
        if (isCategoryCard) {
          return;
        }

        const { key, target } = event;

        if (key === 'Backspace' && !(target as HTMLTextAreaElement).value) {
          setPostFieldsToggling({
            ...postFieldsToggling,
            [field]: { ...postFieldsToggling[field], hidden: true },
          });
        }
      },
      [isCategoryCard, postFieldsToggling]
    );

    const fieldChangeHandler = useCallback(
      (name: keyof ShortPostFields, value: string) => {
        setShortPostFields({ ...shortPostFields, [name]: value });
      },
      [setShortPostFields, shortPostFields]
    );

    const uploadLimit = useMemo(() => {
      if (!hasGallery) {
        return;
      }

      const { image, audio, video, pdf } = gallery[0];

      switch (true) {
        case Boolean(audio):
        case Boolean(video):
        case Boolean(pdf):
          return 1;
        case Boolean(image):
          return 20;
      }
    }, [gallery, hasGallery]);

    const acceptTypes = useMemo(() => {
      return hasGallery || isCategoryCard ? [FileAcceptType.IMAGE] : undefined;
    }, [hasGallery, isCategoryCard]);

    const multiUpload = useMemo(
      () => Boolean(hasGallery && gallery.find(({ image }) => image)),
      [gallery, hasGallery]
    );

    const tagsClickHandler = useCallback(() => {
      setActiveShortPostSettingsTabIndex(CreateCardSettingsTabType.TAGS);
      setSettingsOpen(true);
    }, [setActiveShortPostSettingsTabIndex, setSettingsOpen]);

    const onUpdateHandler = useCallback(
      (text: string) => setShortPostFields({ ...shortPostFields, text }),
      [setShortPostFields, shortPostFields]
    );

    const applePodcastUrlHandler = useCallback(
      async (url: string) => {
        setAppleMetaLoading(true);

        const { source, headline, description, audioLink, imageLink } =
          await getApplePodcastMetaData(url);

        const fallback = () => {
          setLoading(false);
          setAppleMetaLoading(false);
          convertToLinkPost({ url, headline: '' });
        };

        if (!audioLink) {
          fallback();
          return;
        }

        const gallery: GalleryItem[] = [];

        try {
          setLoading(true);

          const parsedAudioData = await storyCardParseUrl({ url: audioLink }).unwrap();

          if (!parsedAudioData) {
            fallback();
            return;
          }

          const parsedImageData = imageLink
            ? await storyCardParseUrl({ url: imageLink }).unwrap()
            : null;

          gallery.push({
            ...(parsedAudioData.gallery[0] ?? {}),
            ...(parsedImageData?.gallery?.[0] ?? {}),
            title: '',
          } as GalleryItem);
        } catch (_) {
          toast.error(t('fileUpload.error'));
        } finally {
          setLoading(false);
          setAppleMetaLoading(false);
        }

        setPostFieldsToggling({
          ...postFieldsToggling,
          sourceName: { ...postFieldsToggling['sourceName'], hidden: isEmptyString(source) },
          headline: { ...postFieldsToggling['headline'], hidden: isEmptyString(headline) },
          subHeadline: { ...postFieldsToggling['headline'], hidden: true },
        } as ShortPostToggleFields);

        shortPostEditorRef.current?.commands.setContent(`<p>${description}</p>`);

        setShortPostFields({
          sourceName: source.slice(0, SOURCE_MAX_LENGTH),
          headline: headline.slice(0, HEADLINE_MAX_LENGTH),
          subHeadline: '',
          text: description,
          gallery,
        });
      },
      [
        convertToLinkPost,
        postFieldsToggling,
        setLoading,
        setShortPostFields,
        shortPostEditorRef,
        storyCardParseUrl,
        t,
      ]
    );

    useEffect(() => {
      if (applePodcastUrl) {
        applePodcastUrlHandler(applePodcastUrl);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onPasteHandler = useCallback(
      async (text: string) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;

        const hasPlainTextLink = urlRegex.test(text);

        if (hasPlainTextLink) {
          const url =
            text
              .replace(/(\r\n|\n|\r)/gm, ' ')
              .split(' ')
              .find((word) => isUrl(word)) ?? '';

          if (shortPostText !== '<p></p>') {
            return;
          }

          if (!url) {
            return;
          }

          if (isApplePodcastUrl(url)) {
            try {
              await applePodcastUrlHandler(url);
            } catch (_) {
              convertToLinkPost({ url, headline: '' });
            }
            return;
          }

          convertToLinkPost({ url, headline: '' });
        }
      },
      [applePodcastUrlHandler, convertToLinkPost, shortPostText]
    );

    return (
      <>
        <div
          className={classNames(classes['short-post'], {
            [classes['short-post--mobile']]: isMobileOnly,
          })}
        >
          <div
            className={classNames(classes['short-post__scrollable'], {
              [classes['short-post__scrollable--mobile']]: isMobileOnly,
            })}
          >
            <TextAreaField
              ref={sourceNameRef}
              inputClassName={classes['short-post__sourceName']}
              value={sourceName}
              placeholder={ctxT('short-post-sourceName')}
              maxLength={SOURCE_MAX_LENGTH}
              disabled={loading}
              onChange={({ target }) => fieldChangeHandler('sourceName', target.value)}
              onKeyDown={(event) => keyDownHandler(event, 'sourceName')}
              hidden={postFieldsToggling?.sourceName?.hidden}
              disableLineBreak
            />

            <TextAreaField
              ref={headlineRef}
              inputClassName={classes['short-post__headline']}
              value={headline}
              placeholder={ctxT('short-post-headline')}
              maxLength={HEADLINE_MAX_LENGTH}
              disabled={loading}
              onChange={({ target }) => fieldChangeHandler('headline', target.value)}
              onKeyDown={(event) => keyDownHandler(event, 'headline')}
              hidden={postFieldsToggling?.headline?.hidden}
            />

            <UserMentionPreview
              mentionedUsers={mentionedUsers}
              {...(hasUserMention && { onClick: () => setUserMentionModalOpen(true) })}
            />

            {editableMedia && (
              <FileUpload
                galleryItems={gallery}
                setGalleryItems={setShortPostGalleryItems}
                loading={loading}
                setLoading={setLoading}
                fileAcceptTypes={acceptTypes}
                uploadLimit={uploadLimit}
                multiUpload={multiUpload}
                files={files}
                fileUrl={fileUrl}
              />
            )}

            <FilePreview
              galleryItems={gallery}
              setGalleryItems={setShortPostGalleryItems}
              editableMedia={editableMedia}
              {...(!isCategoryCard && { hasSettings: true })}
              loading={loading}
            />

            <TextAreaField
              ref={subHeadlineRef}
              inputClassName={classes['short-post__subHeadline']}
              value={subHeadline}
              placeholder={ctxT('short-post-subHeadline')}
              maxLength={SUB_HEADLINE_MAX_LENGTH}
              disabled={loading}
              onChange={({ target }) => fieldChangeHandler('subHeadline', target.value)}
              onKeyDown={(event) => keyDownHandler(event, 'subHeadline')}
              hidden={postFieldsToggling?.subHeadline?.hidden}
            />

            {!appleMetaLoading && (
              <TextEditor
                variant={TextEditorVariant.SHORT_POST}
                editorRef={shortPostEditorRef}
                content={shortPostText}
                onUpdateHandler={(text) => onUpdateHandler(text as string)}
                onPasteHandler={onPasteHandler}
                placeholder={ctxT('short-post-text')}
              />
            )}

            {appleMetaLoading && (
              <div className={classes['short-post__loader']}>
                <Skeleton height={'3rem'} />
              </div>
            )}
          </div>

          <div className={classes['short-post__pane']}>
            <div className={classes['short-post__pane-fields']}>
              {hasUserMention && (
                <IconLabel
                  iconId={'mention'}
                  iconSize={20}
                  label={ctxT('mention')}
                  color={getCssVar('--create-card-link-color')}
                  hoverColor={getCssVar('--create-card-link-hover-color')}
                  onClick={() => setUserMentionModalOpen(true)}
                />
              )}
              {!isCategoryCard && (
                <>
                  {canOpenSettings && hasContentTags && (
                    <IconLabel
                      iconId={'tags'}
                      iconSize={20}
                      label={ctxT('tags')}
                      color={getCssVar('--create-card-link-color')}
                      hoverColor={getCssVar('--create-card-link-hover-color')}
                      onClick={tagsClickHandler}
                    />
                  )}
                  <PostToggleFields
                    loading={loading}
                    fields={postFieldsToggling}
                    setFields={setPostFieldsToggling}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
);
